import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import MSwitch from "../common/switch";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/material.css";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useRecoilState } from "recoil";
import {
  developerEmailState,
  developerIdTokenState,
  originalPathState,
  platformState,
  userInfoState,
} from "../global-state";
import TextField from "@mui/material/TextField";
import { Amplify, Auth } from "aws-amplify";
import { DEV_AUTH_CONFIG } from "../aws-config";
import { useTheme } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import { getUserInfo, saveUserSetting } from "./setting-service";
import { useToast } from "../common/toast";
import InfoIcon from "@mui/icons-material/Info";
import useApiCall from "../common/api-call";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const Developer = () => {
  const history = useHistory();
  const [platform] = useRecoilState(platformState);
  const [developerIdToken, setDeveloperIdToken] = useRecoilState(
    developerIdTokenState
  );
  const { apiCall } = useApiCall();
  const [developerEmail, setDeveloperEmail] =
    useRecoilState(developerEmailState);
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const theme = useTheme();
  const [developerLoggedIn, setDeveloperLoggedIn] = useState(false);
  const [originalPath, setOriginalPath] = useRecoilState(originalPathState);
  const path = useLocation();
  const { successToast, warningToast, errorToast } = useToast();
  const isSmallScreen = useMediaQuery("(max-width:768px)");

  useEffect(() => {
    fetchDeveloperInfo();
  }, []);

  const fetchUserInfo = async () => {
    const resp = await getUserInfo(apiCall, userInfo);
    if (resp) {
      setUserInfo(resp);
      if (platform.source === "app") {
        if (window.Android) {
          window.Android.updateUserInfo(JSON.stringify(resp));
        }
      }
    } else {
      errorToast("Error!");
    }
  };

  const fetchDeveloperInfo = async () => {
    try {
      Amplify.configure(DEV_AUTH_CONFIG);
      const session = await Auth.currentSession();
      const email = session.getIdToken().payload["cognito:username"];
      setDeveloperIdToken(session.getIdToken().getJwtToken());
      setDeveloperEmail(email);
      setDeveloperLoggedIn(true);
    } catch (error) {
      setDeveloperLoggedIn(false);
    }
  };

  const handleScriptModeChange = async (e, v) => {
    const ns = { ...userInfo.setting };
    ns.scriptMode = v;
    await saveSettingChange(ns);
  };

  const handleDebugModeChange = async (e, v) => {
    const ns = { ...userInfo.setting };
    ns.debugMode = v;
    await saveSettingChange(ns);
  };

  const saveSettingChange = async (ns) => {
    const resp = await saveUserSetting(apiCall, ns);
    if (resp) {
      successToast("Saved");
      fetchUserInfo();
    } else {
      errorToast("Error!");
    }
  };

  const handleDeveloperLoginLogout = () => {
    if (developerLoggedIn) {
      handleDeveloperLogout();
    } else {
      handleDeveloperLogin();
    }
  };

  const handleDeveloperLogin = async () => {
    const opath = path.pathname + path.search + path.hash;
    setOriginalPath(opath);
    Amplify.configure(DEV_AUTH_CONFIG);
    try {
      const oauthUrl = `https://${
        DEV_AUTH_CONFIG.Auth.oauth.domain
      }/login?redirect_uri=${
        DEV_AUTH_CONFIG.Auth.oauth.redirectSignIn
      }&response_type=code&client_id=${
        DEV_AUTH_CONFIG.Auth.userPoolWebClientId
      }&state=${encodeURIComponent(opath)}&scope=openid+email`;
      window.location.href = oauthUrl;
    } catch (error) {
      console.error("Error during sign-in", error);
    }
  };

  const handleDeveloperLogout = async () => {
    try {
      Amplify.configure(DEV_AUTH_CONFIG);
      await Auth.signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="start"
      alignItems="start"
      flexDirection="column"
      height="100%"
      p={2}
      width="100%"
    >
      <Box
        position="static"
        sx={{
          display: "flex",
          alignItems: "center",
          justfyContent: "flex-start",
          mb: 2,
        }}
      >
        {isSmallScreen && (
          <IconButton
            color="inherit"
            edge="start"
            sx={{ mr: 1 }}
            onClick={() => {
              history.replace("/setting");
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ display: "flex" }}>
          <Typography variant="h6" style={{ textAlign: "center" }}>
            Developer
          </Typography>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={1} width="100%">
        {developerLoggedIn && (
          <Card
            sx={{
              width: "100%",
              backgroundColor: "background.paper",
              borderRadius: "8px",
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    Advanced
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" gap={1}>
                  {userInfo.setting.engAccount && (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box display="flex" alignItems="center">
                        <Typography variant="body1" component="div">
                          Enable Debug Mode
                        </Typography>
                        <Tooltip title="Enable or disable debug mode.">
                          <IconButton>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <MSwitch
                        checked={userInfo.setting.debugMode}
                        onChange={handleDebugModeChange}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                    </Box>
                  )}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center">
                      <Typography variant="body1" component="div">
                        Enable Scripting
                      </Typography>
                      <Tooltip title="Enable or disable scripting mode.">
                        <IconButton>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <MSwitch
                      checked={userInfo.setting.scriptMode}
                      onChange={handleScriptModeChange}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
        <Card
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: "8px",
            boxShadow: "none",
            transition: "all 0.2s ease",
          }}
        >
          <CardContent>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  Developer
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Box flex={1}>
                  <TextField
                    variant="outlined"
                    value={developerLoggedIn ? developerEmail : "Please login"}
                    disabled
                    fullWidth
                    size="small"
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={handleDeveloperLoginLogout}
                  sx={{ textTransform: "none" }}
                >
                  {developerLoggedIn ? "Logout" : "Login"}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Developer;
